import { useCallback, useRef, useState } from 'react';

import { useDebounceFn } from '@garnish/core';
import { logger } from '@garnish/logger';
import StringCacheMap from 'string-cache-map';

import { fetchPredictions, type Predictions } from './api';

export const usePlacesAutocomplete = (params: Params) => {
  const { apiKey, minChars = 3 } = params;

  // ─── State ─────────────────────────────────────────────────────────────────

  const [value, setValue] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [networkError, setNetworkError] = useState<unknown>();
  const [predictions, setPredictions] = useState<Predictions>([]);

  // ─── Cache ─────────────────────────────────────────────────────────────────

  const predictionsCache = useRef(new StringCacheMap(10));

  // ─── Google Predictions ────────────────────────────────────────────────────

  const getPredictions = useCallback(
    async (input = '') => {
      const validInput = input.trim().length >= minChars;
      let fetchedPredictions: Predictions = [];

      try {
        if (validInput) {
          const { predictions: predictionsList } = await fetchPredictions({
            input,
            apiKey,
          });

          // Enforces results to be valid street addresses, not just locations.
          const validPredictions = predictionsList.filter((prediction) =>
            prediction.types.some(isValidType),
          );

          // add predictions to cache
          predictionsCache.current.set(input, {
            predictions: validPredictions,
          });

          fetchedPredictions = validPredictions;
        }
      } catch (error: unknown) {
        logger.error('[usePlacesAutocomplete]', error);
        setNetworkError(error as Error);
      } finally {
        setPredictions(fetchedPredictions);
        setLoading(false);
      }
    },
    [apiKey, minChars],
  );

  const getPredictionsDebounced = useDebounceFn(getPredictions);

  const setSearchString = useCallback(
    (query: string) => {
      const isSearchable = query.length >= minChars;
      const cachedPredictions = predictionsCache.current.get<PredictionsCacheItem>(query);

      setValue(query);
      setNetworkError(undefined);

      if (cachedPredictions) {
        setPredictions(cachedPredictions.predictions);

        return;
      }

      setLoading(isSearchable);

      if (isSearchable) getPredictionsDebounced(query);
    },
    [getPredictionsDebounced, minChars],
  );

  // ─── Reset Results ─────────────────────────────────────────────────────────

  const clearAutoCompleteResults = useCallback(() => {
    setValue(undefined);
    setLoading(false);
    setNetworkError(undefined);
    setPredictions([]);
  }, []);

  // ───────────────────────────────────────────────────────────────────────────

  const validInput = (value ?? '').length >= minChars;
  const noResults = !loading && validInput && predictions.length === 0;

  return {
    searchString: value ?? '',
    predictions,
    loading,
    error: networkError,
    noResults,
    setSearchString,
    clearAutoCompleteResults,
  };
};

// ──── Helpers ────────────────────────────────────────────────────────────────

const isValidType = (type: string) => {
  return validTypes.has(type);
};

// ─── Constants ───────────────────────────────────────────────────────────────

// @see https://developers.google.com/maps/documentation/places/web-service/supported_types
const validTypes = new Set(['street_address', 'premise', 'subpremise']);

type Params = {
  apiKey: string;
  minChars?: number;
};

type PredictionsCacheItem = {
  predictions: Predictions;
};
